import React from 'react';
import { Button, Flex, Modal, App, Typography } from 'antd';
import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';

import ButtonWithRole, { useIsDisabledForRole, useIsRoleIn } from '../../../components/ButtonWithRole';
import { UserRole } from '../../PrerenderUser';
import { useCharbeePlanId } from '../../../hooks/usePrerenderUser';
import { useEvent } from '../../events/hooks/useEvent';
import { CustomTooltip } from '../../../components/Tooltip';
import { CONTENT_WRITE_ROLES } from '../../auth/roleCapabilities';
import { useDeleteDomainMutation } from '../api/domainManagerApiSlice';

// DELETE /v3/domain-statistics/:id admits superAdmin and admin only
// (services/rest-api/src/domain-statistics/domain-statistics.controller.ts). Deleting is not the
// same right as verifying, so the two controls carry two lists.
const CANNOT_DELETE_DOMAIN = [UserRole.BillingManager, UserRole.TeamMember, UserRole.Guest];

const VERIFY_TOOLTIP = 'Verify Prerender integration';
const VERIFY_DISABLED_MESSAGE = 'Only an account owner, an admin or a team member can verify a domain.';
const DELETE_TOOLTIP = 'Delete domain';
const DELETE_DISABLED_MESSAGE = 'Only an account owner or an admin can delete a domain.';

type ActionTooltipProps = {
  message: string;
  disabled: boolean;
};

// Browsers suppress mouse events over a disabled control, so a bare anchor around one never sees
// the hover and the tooltip never opens. The inner `pointer-events: none` span takes the hover
// instead — the shape RerunPlanGate uses. It wraps the button only while the button is disabled,
// because that span swallows the clicks of an enabled one.
function ActionTooltip({ message, disabled, children }: React.PropsWithChildren<ActionTooltipProps>) {
  return (
    <CustomTooltip placement="top" message={message} wrapperStyle={disabled ? { cursor: 'not-allowed' } : undefined}>
      {disabled ? <span style={{ pointerEvents: 'none' }}>{children}</span> : children}
    </CustomTooltip>
  );
}

type ActionsCellProps = {
  domain: string;
  numberOfUrls: string;
  id: number;
  verified: boolean;
  disabled?: boolean;
  onVerifyIntegration: (domain: string) => void;
};

function ActionsCell({ domain, id, numberOfUrls, verified = false, onVerifyIntegration, disabled }: ActionsCellProps) {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteDomain, deleteDomainResult] = useDeleteDomainMutation();

  const { message } = App.useApp();
  const chargebeePlanId = useCharbeePlanId();
  // The role denial and the disabled state are kept apart so the tooltip never blames the role for
  // a row the caller disabled for its own reason.
  const verifyDeniedForRole = !useIsRoleIn(CONTENT_WRITE_ROLES);
  const deleteDeniedForRole = useIsDisabledForRole(CANNOT_DELETE_DOMAIN);
  const verifyDisabled = verifyDeniedForRole || Boolean(disabled);
  const deleteDisabled = deleteDeniedForRole || Boolean(disabled);

  const { track } = useEvent();

  const onDelete = () => {
    deleteDomain(id)
      .unwrap()
      .then(() => {
        message.success('Domain deleted successfully');
        track('Domain Deleted', { domain, subscription_plan: chargebeePlanId, numberOfUrls });
      })
      .catch(() => {
        message.error('Failed to delete domain');
      })
      .finally(() => setDeleteModalOpen(false));
  };

  return (
    <Flex gap={8} justify="flex-start">
      <Modal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        title={`Deleting domain: ${domain}`}
        closable
        okText="Delete"
        okButtonProps={{ danger: true, type: 'primary', loading: deleteDomainResult.isLoading }}
        onOk={onDelete}
      >
        {parseInt(numberOfUrls) === 0 ? (
          <Typography.Paragraph>
            Are you sure you want to delete this domain <Typography.Text code>{domain}</Typography.Text>? This action is
            irreversible.
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph>
            Removing this domain <Typography.Text code>{domain}</Typography.Text>{' '}
            <Typography.Text strong>will remove all {numberOfUrls}</Typography.Text> associated pages from the cache.
            <br /> Please note the cache clear process might take some time.
          </Typography.Paragraph>
        )}
      </Modal>
      {!verified && (
        <ActionTooltip
          message={verifyDeniedForRole ? VERIFY_DISABLED_MESSAGE : VERIFY_TOOLTIP}
          disabled={verifyDisabled}
        >
          <Button icon={<CheckOutlined />} onClick={() => onVerifyIntegration(domain)} disabled={verifyDisabled} />
        </ActionTooltip>
      )}
      <ActionTooltip message={deleteDeniedForRole ? DELETE_DISABLED_MESSAGE : DELETE_TOOLTIP} disabled={deleteDisabled}>
        <ButtonWithRole
          danger
          disabledFor={CANNOT_DELETE_DOMAIN}
          icon={<DeleteOutlined />}
          loading={deleteDomainResult.isLoading}
          disabled={disabled}
          onClick={() => setDeleteModalOpen(true)}
        />
      </ActionTooltip>
    </Flex>
  );
}

export default React.memo(ActionsCell);
